// web/src/components/KidAvatar.tsx
// Circular avatar with optional kit-colour ring. Photo if provided,
// initials fallback otherwise. Used in:
//  - KidDetail hero (size='xl')
//  - Kids strip on Dashboard (size='md')
//  - KidCard rows (size='sm')
//
// Kit-colour ring is the parent's chosen `kitColour` — could be a CSS
// colour name ("red") or a hex string ("#1F3D2C"). We don't validate;
// invalid values fall back to var(--green).

import type { CSSProperties } from 'react';

export interface KidAvatarProps {
  name: string;
  /** Optional URL to a kid photo (signed). */
  photoUrl?: string | null;
  /** Optional kit colour for the ring (CSS colour). */
  kitColour?: string;
  /** Optional squad number rendered as a tiny corner badge. */
  squadNumber?: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  style?: CSSProperties;
}

const SIZE_PX: Record<NonNullable<KidAvatarProps['size']>, number> = {
  sm: 48,
  md: 72,
  lg: 120,
  xl: 200,
};

const RING_PX: Record<NonNullable<KidAvatarProps['size']>, number> = {
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('');
}

/**
 * Picks a readable ink (light or dark) given a CSS colour. Uses YIQ
 * approximation on hex inputs; falls back to white for unparseable values
 * (named colours, etc.) which works for our usual saturated kit colours.
 */
function readableInkFor(colour: string | undefined): string {
  if (!colour) return 'var(--ink-on-green)';
  const hex = colour.trim().match(/^#?([0-9a-f]{6})$/i)?.[1];
  if (!hex) return 'var(--ink-on-green)';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 165 ? '#2a1d0c' : 'var(--ink-on-green)';
}

export function KidAvatar({
  name,
  photoUrl,
  kitColour,
  squadNumber,
  size = 'md',
  style,
}: KidAvatarProps) {
  const px = SIZE_PX[size];
  const ring = RING_PX[size];
  const fill = kitColour || 'var(--green)';
  const ink = readableInkFor(kitColour);
  const initials = initialsOf(name);

  const labelFontSize =
    size === 'xl' ? '4rem'
    : size === 'lg' ? '2.5rem'
    : size === 'md' ? '1.5rem'
    : '1rem';

  return (
    <div
      style={{
        position: 'relative',
        width: px,
        height: px,
        flexShrink: 0,
        ...style,
      }}
      aria-label={name}
    >
      {/* Outer thin paper ring — separates the kit colour from any
          dark/light background it sits on. Always cream. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: `${ring}px solid var(--bg)`,
          boxShadow: 'var(--shadow-1)',
        }}
        aria-hidden
      />
      {/* Inner circle — KIT COLOUR is the dominant fill. Photo overlays
          when present; otherwise initials sit on the kit colour. */}
      <div
        style={{
          position: 'absolute',
          inset: ring + 2,
          borderRadius: '50%',
          overflow: 'hidden',
          background: photoUrl ? '#000' : fill,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span
            style={{
              fontFamily: 'var(--font-masthead)',
              fontStyle: 'italic',
              fontSize: labelFontSize,
              color: ink,
              lineHeight: 1,
            }}
          >
            {initials}
          </span>
        )}
      </div>

      {/* Squad number badge */}
      {typeof squadNumber === 'number' ? (
        <div
          style={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            minWidth: Math.max(24, px * 0.22),
            height: Math.max(24, px * 0.22),
            padding: '0 6px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--gold)',
            color: '#2a1d0c',
            fontFamily: 'var(--font-masthead)',
            fontStyle: 'italic',
            display: 'grid',
            placeItems: 'center',
            fontSize: Math.max(11, px * 0.13),
            border: '2px solid var(--bg)',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          {squadNumber}
        </div>
      ) : null}
    </div>
  );
}
